import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import { BaseError } from '@utils/types/shared/base';
import {
  UsageSummaryResponse,
  UsageSummaryCustomerType
} from '@utils/types/back-office/usage-revenue';

/**
 * Hook to fetch usage summary data for a given month
 *
 * GET /api/admin/usage/summary
 * Query params:
 *   - month (optional, default: current month, format: YYYY-MM)
 *   - customer_type (optional: all | direct | reseller, default: all)
 *
 * Returns summary with total_usage, total_cost, total_billed, and forecast
 */
export const useGetUsageSummary = (
  month: string | undefined,
  customerType: UsageSummaryCustomerType = 'all'
) => {
  const fetchSummary = async () => {
    const params = new URLSearchParams();
    if (month) {
      params.append('month', month);
    }
    if (customerType && customerType !== 'all') {
      params.append('customer_type', customerType);
    }

    const queryString = params.toString();
    const url = `${URL_EXTENSION}/admin/usage/summary${queryString ? `?${queryString}` : ''}`;

    const response = await Axios.get<UsageSummaryResponse>(url);
    return response.data;
  };

  return useQuery<UsageSummaryResponse, AxiosError<BaseError>>(
    ['usageSummary', month, customerType],
    fetchSummary,
    {
      enabled: !!month,
      useErrorBoundary: false,
      // Refetch every 5 minutes since summary data changes
      refetchInterval: 5 * 60 * 1000
    }
  );
};
